import type { ResolvedPos } from '@tiptap/pm/model';
import type { KeyboardShortcutCommand } from '@tiptap/react';
import type { DeepPartial } from 'utility-types';

import { TIPTAP_EMBED_TYPE, TIPTAP_LINK_PREVIEW_TYPE } from './../../ricos-content';
import { convertBlockDataToRicos } from './../../ricos-content/lib/convertBlockDataToRicos';
import type { LinkPreviewData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type {
  ExtensionProps,
  NodeConfig,
  RicosExtension,
  RicosExtensionConfig,
} from './../../ricos-types';
import {
  createRicosFunctionalExtension,
  createRicosNodeExtension,
} from './../../plugin-commons';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';

import {
  createLinkPreviewData,
  fetchLinkPreview,
  shouldAddEmbed,
  shouldAddLinkPreview,
} from '../lib/utils';
import type { LinkPreviewPluginEditorConfig } from '../types';
import { LINK_PREVIEW_TYPE } from '../types';
import { Embed } from './embed';
import { LinkPreview } from './link-preview';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkPreview: {
      /**
       * Set a linkPreview node
       */
      setLinkPreview: (attributes: DeepPartial<LinkPreviewData>) => ReturnType;
    };
  }
}

//// Regexes should be shared with link extension

/**
 * A regex that matches any string that contains a link
 */
const urlRegex =
  // eslint-disable-next-line max-len
  /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)/gi;

/**
 * A regex that matches an url
 */
const urlRegexExact = new RegExp('^' + urlRegex.source + '$', 'gi');

const isLinkOnlyBlock = ({ parent, nodeBefore }: ResolvedPos): boolean =>
  parent.childCount === 1 && nodeBefore?.text?.search(urlRegexExact) !== -1;

const addLinkPreview =
  (config: LinkPreviewPluginEditorConfig): KeyboardShortcutCommand =>
  ({ editor }) => {
    const pos = editor.state.selection.$anchor;
    const {
      enableEmbed = true,
      enableLinkPreview = true,
      fetchData,
      link: linkConfig,
    } = config || {};
    if (
      pos.nodeBefore?.text &&
      isLinkOnlyBlock(pos) &&
      fetchData &&
      (enableEmbed || enableLinkPreview)
    ) {
      let isPreviewAdded = false;
      const { target, rel } = linkConfig || {};
      const url = pos.nodeBefore?.text;
      fetchLinkPreview(fetchData, url)
        .then(linkPreviewData => {
          const shouldEmbed = shouldAddEmbed(
            linkPreviewData.html,
            enableEmbed,
            linkPreviewData.fixedUrl
          );
          if (shouldEmbed || shouldAddLinkPreview(linkPreviewData.title, enableLinkPreview)) {
            isPreviewAdded = true;
            return createLinkPreviewData({ url, target, rel }, linkPreviewData, config).then(
              linkPreviewComponentData => {
                const convertedLinkPreviewData = convertBlockDataToRicos(
                  LINK_PREVIEW_TYPE,
                  linkPreviewComponentData
                );
                editor
                  .chain()
                  .focus()
                  .deleteRange({ from: pos.pos - (pos.nodeBefore?.nodeSize || 0), to: pos.pos })
                  .insertContent([
                    {
                      type: shouldEmbed ? TIPTAP_EMBED_TYPE : TIPTAP_LINK_PREVIEW_TYPE,
                      attrs: convertedLinkPreviewData,
                    },
                    { type: 'PARAGRAPH' },
                  ])
                  .run();
              }
            );
          }
        })
        .finally(() => {});
      return isPreviewAdded;
    }
    return false;
  };

export const tiptapExtensions = [
  createRicosNodeExtension({
    name: TIPTAP_LINK_PREVIEW_TYPE,
    groups: ['overlay', 'draggable', 'not-overtypable'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>
    ) => ({
      ...config,
      addOptions: () => settings,
    }),
    Component: LinkPreview,
    createExtensionConfig() {
      return {
        group: 'block layoutCellContent collapsibleListItemBodyContent tableCellContent',
        selectable: true,
        draggable: true,
        name: this.name,
        addAttributes: () => ({
          containerData: null,
          link: null,
          title: null,
          thumbnailUrl: null,
          description: null,
          html: null,
        }),
        addCommands() {
          return {
            setLinkPreview:
              attributes =>
              ({ commands }) => {
                return commands.setNode('linkPreview', attributes);
              },
          };
        },
      };
    },
  }),
  createRicosNodeExtension({
    name: TIPTAP_EMBED_TYPE,
    groups: ['overlay', 'draggable', 'not-overtypable'],
    type: 'react-node',
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>
    ) => ({
      ...config,
      addOptions: () => settings,
    }),
    Component: Embed,
    createExtensionConfig() {
      return {
        group: 'block layoutCellContent collapsibleListItemBodyContent tableCellContent',
        selectable: true,
        draggable: true,
        name: this.name,
        addAttributes: () => ({
          containerData: null,
          oembed: null,
          src: null,
        }),
      };
    },
  }),
  createRicosFunctionalExtension({
    type: 'extension',
    groups: ['shortcuts-enabled'],
    name: 'linkPreviewEnter',
    reconfigure: (
      config: RicosExtensionConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>,
      services: TiptapAdapterServices
    ) => ({
      ...config,
      addKeyboardShortcuts() {
        const newLine = addLinkPreview(settings);

        const tiptapKeyboardShortcuts = {
          Enter: newLine,
        };

        const newLineShortcuts: any = [];
        //@ts-ignore
        services.shortcuts.shortcuts.forEach(shortcut => {
          if (shortcut.shortcut.name === 'NewLine') {
            newLineShortcuts.push(shortcut);
          }
        });

        newLineShortcuts.forEach((newLineShortcut: $TSFixMe) => {
          const modifier = newLineShortcut.keys.macOs.modifiers[0];
          const key = newLineShortcut.keys.macOs.key;
          const shortcutKey = modifier ? `${modifier}-${key}` : key;
          tiptapKeyboardShortcuts[shortcutKey as $TSFixMeKey<typeof tiptapKeyboardShortcuts>] =
            newLine;
        });

        return {
          ...tiptapKeyboardShortcuts,
        };
      },
    }),
    createExtensionConfig() {
      return {
        name: this.name,
        priority: 101,
      };
    },
  }),
];
